import {
  Anchor,
  Box,
  Button,
  Center,
  Collapse,
  Divider,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { IconBrandWindows, IconAlertCircle, IconMail } from '@tabler/icons-react';
import { useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';
import { useLocalAuth } from '../auth/local-auth';
import { apiClient } from '../api/http';
import { getErrorMessage } from '../api/client';
import { Brand } from '../components/Brand';

/**
 * Login unificado del backoffice (Sprint 3):
 *  - Email + password (form principal, recomendado para autonomos nuevos).
 *  - Microsoft / Entra ID (alternativa para empresas con M365 corporativo).
 *  - Enlace visible a /signup.
 *
 * Sprint 5.1: si el login local falla con 403 (email no verificado), el
 * formulario muestra inline un CTA para reenviar el email de verificacion.
 *
 * Cualquier sesion (local o MSAL) redirige a la URL original si la habia.
 */
const URL_PARAMS = new URLSearchParams(window.location.search);
const USE_MOCK_AUTH = URL_PARAMS.get('mockAuth') === 'true';

export function Login() {
  const { instance, accounts } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const local = useLocalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname ?? '/';

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<'generic' | 'unverified'>('generic');
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  // Si ya estamos autenticados (local o MSAL) salimos hacia la pagina destino.
  useEffect(() => {
    const hasMsalSession = USE_MOCK_AUTH
      ? accounts.length > 0
      : isMsalAuthenticated && accounts.length > 0;

    if (local.isAuthenticated || hasMsalSession) {
      navigate(from, { replace: true });
    }
  }, [local.isAuthenticated, isMsalAuthenticated, accounts.length, from, navigate]);

  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Email no valido'),
      password: (v) => (v.length > 0 ? null : 'Password requerida'),
    },
  });

  const handleLocalLogin = async (values: typeof form.values) => {
    setError(null);
    setErrorKind('generic');
    setResendStatus('idle');
    setSubmitting(true);
    try {
      await local.login({ email: values.email.trim(), password: values.password });
      navigate(from, { replace: true });
    } catch (e) {
      const msg = getErrorMessage(e);
      setError(msg);
      const ax = e as { response?: { status?: number } };
      if (ax?.response?.status === 403) {
        setErrorKind('unverified');
        setPendingEmail(values.email.trim());
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleMsalLogin = () => {
    instance.loginRedirect({ ...loginRequest, state: from });
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    setResendStatus('sending');
    try {
      await apiClient.post('/auth/resend-verification', { email: pendingEmail });
    } catch {
      /* anti-enumeracion: el backend siempre responde 200 salvo error de red */
    }
    setResendStatus('sent');
  };

  return (
    <Center mih="100vh" bg="deca.0">
      <Box p="xl" bg="white" style={{ borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Stack align="center" gap="md" maw={400}>
          <Brand />
        </Stack>

        {error && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={20} />} mt="md">
            {error}
          </Alert>
        )}

        <form onSubmit={form.onSubmit(handleLocalLogin)}>
          <Stack mt="lg">
            <TextInput
              label="Email"
              type="email"
              placeholder="juan@miempresa.com"
              required
              {...form.getInputProps('email')}
            />
            <PasswordInput label="Password" required {...form.getInputProps('password')} />
            <Anchor component={Link} to="/forgot-password" size="sm" ta="right">
              ¿Olvidaste tu contraseña?
            </Anchor>
            <Button type="submit" color="deca.9" fullWidth loading={submitting}>
              Entrar
            </Button>
          </Stack>
        </form>

        <Collapse in={errorKind === 'unverified'}>
          <Stack gap="xs" mt="md">
            <Text size="sm" ta="center" c="dimmed">
              No has verificado tu email. Podemos enviarte un nuevo enlace.
            </Text>
            {resendStatus === 'sent' ? (
              <Alert color="green" variant="light" icon={<IconMail size={18} />}>
                Si el email existe, te hemos enviado un nuevo enlace de verificacion. Revisa tu
                bandeja (y la carpeta de spam).
              </Alert>
            ) : (
              <Button
                variant="light"
                color="deca.9"
                leftSection={<IconMail size={16} />}
                loading={resendStatus === 'sending'}
                onClick={handleResend}
              >
                Reenviar verificacion a {pendingEmail}
              </Button>
            )}
          </Stack>
        </Collapse>

        <Divider label="o" labelPosition="center" my="md" />

        <Stack>
          <Button
            variant="default"
            leftSection={<IconBrandWindows size={18} />}
            onClick={handleMsalLogin}
            fullWidth
          >
            Continuar con Microsoft
          </Button>
          <Text size="xs" c="dimmed" ta="center">
            Si tu empresa usa Microsoft Entra ID (Azure AD).
          </Text>
        </Stack>

        <Text size="sm" c="dimmed" ta="center" mt="lg">
          Eres nuevo?{' '}
          <Anchor component={Link} to="/signup" fw={600}>
            Crea una cuenta gratis
          </Anchor>
        </Text>
      </Box>
    </Center>
  );
}
